import type { CSSProperties } from "react";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { BoardView } from "../features/BoardView";
import { EvidenceView } from "../features/EvidenceView";
import { GraphView } from "../features/GraphView";
import { MembersView } from "../features/MembersView";
import { OverviewView } from "../features/OverviewView";
import { RoadmapView } from "../features/RoadmapView";
import { TimelineView } from "../features/TimelineView";
import { roleForProject, useWorkspace } from "../features/workspace-context";

const views = [
  ["overview", "Resumen"],
  ["roadmap", "Roadmap"],
  ["board", "Board"],
  ["map", "Mapa"],
  ["timeline", "Timeline"],
  ["evidence", "Evidencia"],
  ["members", "Miembros"],
] as const;

type ViewName = (typeof views)[number][0];

export function ProjectPage() {
  const { projectId, view } = useParams();
  const {
    data,
    session,
    updateWorkItemStatus,
    createWorkItem,
    addMemberByHandle,
    updateMemberRole,
    removeMember,
  } = useWorkspace();
  if (!data || !session || !projectId) return null;
  const project = data.projects.find((candidate) => candidate.id === projectId);
  if (!project) return <Navigate to="/" replace />;

  const activeView = (views.some(([name]) => name === view) ? view : "overview") as ViewName;
  const items = data.workItems.filter((item) => item.projectId === project.id);
  const dependencies = data.dependencies.filter(
    (dependency) => dependency.projectId === project.id,
  );
  const links = data.links.filter((link) => link.projectId === project.id);
  const milestones = data.milestones.filter((milestone) => milestone.projectId === project.id);
  const history = data.history.filter((entry) => entry.projectId === project.id);
  const snapshots = data.snapshots.filter((snapshot) => snapshot.projectId === project.id);
  const members = data.members.filter((member) => member.projectId === project.id);
  const role = roleForProject(data, project.id, session.userId);

  const content = (() => {
    switch (activeView) {
      case "roadmap":
        return (
          <RoadmapView
            items={items}
            role={role}
            projectId={project.id}
            onStatusChange={updateWorkItemStatus}
            onCreate={createWorkItem}
          />
        );
      case "board":
        return <BoardView items={items} role={role} onStatusChange={updateWorkItemStatus} />;
      case "map":
        return <GraphView project={project} items={items} dependencies={dependencies} />;
      case "timeline":
        return <TimelineView history={history} items={items} />;
      case "evidence":
        return <EvidenceView links={links} items={items} />;
      case "members":
        return (
          <MembersView
            members={members}
            currentUserId={session.userId}
            currentRole={role}
            onAdd={(handle, memberRole) => addMemberByHandle(project.id, handle, memberRole)}
            onRoleChange={(userId, memberRole) => updateMemberRole(project.id, userId, memberRole)}
            onRemove={(userId) => removeMember(project.id, userId)}
          />
        );
      default:
        return (
          <OverviewView
            project={project}
            items={items}
            milestones={milestones}
            snapshots={snapshots}
          />
        );
    }
  })();

  return (
    <div className="project-page" style={{ "--project-accent": project.accent } as CSSProperties}>
      <header className="project-hero">
        <div>
          <div className="project-hero__meta">
            <span className={`project-state project-state--${project.status}`}>
              {project.status === "active"
                ? "Activo"
                : project.status === "paused"
                  ? "Pausado"
                  : "Archivado"}
            </span>
            <span className={`role-pill role-pill--${role ?? "viewer"}`}>
              {role ?? "sin acceso"}
            </span>
          </div>
          <span className="eyebrow">{project.slug}</span>
          <h1>{project.name}</h1>
          <p>{project.summary}</p>
        </div>
        <div className="project-hero__signal" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </header>

      <nav className="project-tabs" aria-label="Vistas del proyecto">
        {views.map(([name, label]) => (
          <NavLink
            key={name}
            to={`/projects/${project.id}/${name}`}
            className={({ isActive }) =>
              isActive ? "project-tab project-tab--active" : "project-tab"
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>
      {content}
    </div>
  );
}
